import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnChanges, SimpleChanges, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ComissaoAtividadeResponsavelFiltroRequest, ComissaoAtividadeResponsavelResponse } from '@/core/api/sicom-api/comissoes/atividades/comissao-atividade.model';
import { FiltrosAtividadesComissoes, OPCOES_SELECAO_ATIVIDADE, OPCOES_SITUACAO_ATIVIDADE, OPCOES_TIPO_COMISSAO_ATIVIDADE, criarFiltrosAtividadesIniciais } from '@/features/comissoes/dashboard-comissoes/atividades-comissoes.model';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type BlocoFiltroAtividadeColapsavel = 'codigo' | 'periodo' | 'situacao' | 'responsavel' | 'descricao' | 'tipoComissao';
type EstadoBlocosColapsados = Record<BlocoFiltroAtividadeColapsavel, boolean>;

@Component({
    selector: 'app-filtros-atividades-comissoes',
    standalone: true,
    imports: [DialogoPadraoDirective, CommonModule, FormsModule, ButtonModule, DialogModule, SelectEstado],
    templateUrl: './filtros-atividades-comissoes.html',
    styleUrls: ['./filtros-atividades-comissoes.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Barra de filtros do dashboard de atividades: edita uma cópia local e só aplica no "Filtrar";
 *  abre um diálogo de pesquisa de responsável por UF/nome. */
export class FiltrosAtividadesComissoesComponent implements OnChanges {
    /** Filtros aplicados vindos do pai (copiados para `filtrosLocais`). */
    readonly filtros = input<FiltrosAtividadesComissoes>(criarFiltrosAtividadesIniciais());
    /** Resultados da pesquisa de responsáveis (no diálogo). */
    readonly responsaveisPesquisa = input<ComissaoAtividadeResponsavelResponse[]>([]);
    /** Indica pesquisa de responsáveis em andamento. */
    readonly carregandoResponsaveisPesquisa = input(false);
    /** Mensagem de erro da pesquisa de responsáveis. */
    readonly erroPesquisaResponsaveis = input('');

    /** Emite os filtros ao "Filtrar". */
    readonly aplicarFiltros = output<FiltrosAtividadesComissoes>();
    /** Emite ao "Limpar". */
    readonly limparFiltros = output<void>();
    /** Pede a pesquisa de responsáveis (ao abrir/filtrar o diálogo). */
    readonly pesquisarResponsaveis = output<ComissaoAtividadeResponsavelFiltroRequest>();

    filtrosLocais: FiltrosAtividadesComissoes = criarFiltrosAtividadesIniciais();
    exibirDialogResponsaveis = false;
    filtroResponsavelUf = 'BR';
    filtroResponsavelNome = '';
    responsavelSelecionadoPesquisa: ComissaoAtividadeResponsavelResponse | null = null;

    readonly opcoesSelecao = OPCOES_SELECAO_ATIVIDADE;
    readonly opcoesSituacao = OPCOES_SITUACAO_ATIVIDADE;
    readonly opcoesTipoComissao = OPCOES_TIPO_COMISSAO_ATIVIDADE;

    blocosColapsados: EstadoBlocosColapsados = {
        codigo: true,
        periodo: true,
        situacao: true,
        responsavel: true,
        descricao: true,
        tipoComissao: true
    };
    private estadoAntesDoColapso: EstadoBlocosColapsados | null = {
        codigo: false,
        periodo: false,
        situacao: false,
        responsavel: false,
        descricao: false,
        tipoComissao: false
    };

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['filtros']) {
            this.filtrosLocais = { ...this.filtros() };
        }
    }

    aplicar(): void {
        this.aplicarFiltros.emit({ ...this.filtrosLocais });
    }

    limpar(): void {
        this.filtrosLocais = criarFiltrosAtividadesIniciais();
        this.limparFiltros.emit();
        this.aplicarFiltros.emit({ ...this.filtrosLocais });
    }

    colapsarFiltros(): void {
        if (!this.estadoAntesDoColapso) {
            this.estadoAntesDoColapso = { ...this.blocosColapsados };
            this.blocosColapsados = {
                codigo: true,
                periodo: true,
                situacao: true,
                responsavel: true,
                descricao: true,
                tipoComissao: true
            };
            return;
        }

        this.blocosColapsados = { ...this.estadoAntesDoColapso };
        this.estadoAntesDoColapso = null;
    }

    labelBotaoColapso(): string {
        return this.estadoAntesDoColapso ? 'Restaurar Filtros' : 'Colapsar Filtros';
    }

    alternarColapso(chave: BlocoFiltroAtividadeColapsavel): void {
        this.blocosColapsados[chave] = !this.blocosColapsados[chave];
    }

    iconeColapso(chave: BlocoFiltroAtividadeColapsavel): string {
        return this.blocosColapsados[chave] ? 'pi pi-chevron-down' : 'pi pi-chevron-up';
    }

    abrirPesquisaResponsavel(): void {
        this.exibirDialogResponsaveis = true;
        this.filtroResponsavelUf = this.filtrosLocais.responsavelUf || 'BR';
        this.filtroResponsavelNome = this.normalizarNomePesquisaResponsavel(this.filtrosLocais.responsavelNome || '');
        this.responsavelSelecionadoPesquisa = this.criarResponsavelSelecionadoAtual();
        this.pesquisarResponsaveisDialog();
    }

    fecharPesquisaResponsavel(): void {
        this.exibirDialogResponsaveis = false;
    }

    pesquisarResponsaveisDialog(): void {
        this.pesquisarResponsaveis.emit(this.montarRequestPesquisaResponsavel());
    }

    selecionarResponsavelPesquisa(responsavel: ComissaoAtividadeResponsavelResponse): void {
        this.responsavelSelecionadoPesquisa = responsavel;
    }

    onFiltroResponsavelNomeChange(valor: string): void {
        this.filtroResponsavelNome = this.normalizarNomePesquisaResponsavel(valor);
    }

    isResponsavelSelecionado(responsavel: ComissaoAtividadeResponsavelResponse): boolean {
        if (!this.responsavelSelecionadoPesquisa) {
            return false;
        }

        return this.responsavelSelecionadoPesquisa.idSolicitante === responsavel.idSolicitante && this.normalizarUf(this.responsavelSelecionadoPesquisa.ufSgUf) === this.normalizarUf(responsavel.ufSgUf);
    }

    confirmarPesquisaResponsavel(): void {
        if (!this.responsavelSelecionadoPesquisa?.idSolicitante || !this.responsavelSelecionadoPesquisa.ufSgUf) {
            return;
        }

        this.filtrosLocais.responsavelId = this.responsavelSelecionadoPesquisa.idSolicitante;
        this.filtrosLocais.responsavelUf = this.normalizarUf(this.responsavelSelecionadoPesquisa.ufSgUf);
        this.filtrosLocais.responsavelNome = (this.responsavelSelecionadoPesquisa.nmSolicitante ?? '').trim();

        this.fecharPesquisaResponsavel();
    }

    limparResponsavelSelecionado(): void {
        this.filtrosLocais.responsavelId = null;
        this.filtrosLocais.responsavelUf = '';
        this.filtrosLocais.responsavelNome = '';
    }

    descricaoResponsavelSelecionado(): string {
        if (!this.filtrosLocais.responsavelId || !this.filtrosLocais.responsavelUf) {
            return '';
        }

        const nome = this.filtrosLocais.responsavelNome.trim();
        const sufixoNome = nome ? ` - ${nome}` : '';
        return `${this.filtrosLocais.responsavelId}/${this.filtrosLocais.responsavelUf}${sufixoNome}`;
    }

    trackResponsavel(index: number, item: ComissaoAtividadeResponsavelResponse): string {
        const id = item.idSolicitante ?? 0;
        const uf = this.normalizarUf(item.ufSgUf);
        return `${id}-${uf}-${index}`;
    }

    private criarResponsavelSelecionadoAtual(): ComissaoAtividadeResponsavelResponse | null {
        if (!this.filtrosLocais.responsavelId || !this.filtrosLocais.responsavelUf) {
            return null;
        }

        return {
            idSolicitante: this.filtrosLocais.responsavelId,
            ufSgUf: this.filtrosLocais.responsavelUf,
            nmSolicitante: this.filtrosLocais.responsavelNome
        };
    }

    private montarRequestPesquisaResponsavel(): ComissaoAtividadeResponsavelFiltroRequest {
        const nome = this.normalizarNomePesquisaResponsavel(this.filtroResponsavelNome).trim();
        const uf = this.normalizarUf(this.filtroResponsavelUf);
        const idReuniao = this.obterIdReuniaoSelecionado();

        return {
            idReuniao,
            idEstruturaOrganizacional: this.obterIdComissaoSelecionada(),
            nmSolicitante: nome || undefined,
            ufSgUf: uf === 'BR' ? undefined : uf
        };
    }

    private obterIdReuniaoSelecionado(): number | undefined {
        if (this.filtrosLocais.tipoSelecao !== 'codigo-reuniao') {
            return undefined;
        }

        const numero = Number(this.filtrosLocais.valorSelecao);
        if (!Number.isFinite(numero) || numero <= 0) {
            return undefined;
        }

        return Math.trunc(numero);
    }

    private obterIdComissaoSelecionada(): number | undefined {
        if (this.filtrosLocais.tipoSelecao !== 'codigo-comissao') {
            return undefined;
        }

        const numero = Number(this.filtrosLocais.valorSelecao);
        if (!Number.isFinite(numero) || numero <= 0) {
            return undefined;
        }

        return Math.trunc(numero);
    }

    private normalizarUf(uf?: string | null): string {
        return (uf ?? '').trim().toUpperCase();
    }

    private normalizarNomePesquisaResponsavel(valor: string): string {
        return valor.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\s]/g, '').toUpperCase();
    }
}
